import {Injectable} from '@angular/core';
import {SpecsAndVariants} from '../../../../models';
import {ProductSpec} from '../../../../models/clare';
import {concat, exhaustMap, forkJoin, Observable, of, toArray} from 'rxjs';
import {ProductApiService} from '../../../../services/product/product-api.service';
import {SpecApiService} from '../../../../services/spec-api.service';
import {Product, SpecOption, Variation} from '@sportngin/se-commerce-sdk/models/clare';

@Injectable({providedIn: 'root'})
export class UpdateSpecsAndVariantsService {
  constructor(
    private productApi: ProductApiService,
    private specApi: SpecApiService
  ) {}

  updateSpecsAndGenerateVariants(
    specsAndVariants: SpecsAndVariants,
    item: Product,
    itemVariations: Variation[]
  ): Observable<[variants: Variation[], specs: ProductSpec[]]> {
    const createSpecs$ = this.createSpecs(specsAndVariants, item);
    const createOptions$ = this.createOptions(specsAndVariants);
    const updateVariants$ = this.updateVariants(specsAndVariants);

    const generateVariants$ =
      specsAndVariants.updateSpecs.specs.length > 0 || specsAndVariants.updateSpecs.optionsToAdd.length > 0
        ? itemVariations.length > 0
          ? this.productApi.updateGenerateVariants(item.id!)
          : this.productApi.generateProductVariants(item.id!)
        : of({});

    // Create specs and options sequentially to preserve consistent variant
    // grouping order on the backend (first spec options created first).
    return concat(...createSpecs$, ...createOptions$).pipe(
      toArray(),
      exhaustMap(() => forkJoin(updateVariants$)),
      exhaustMap(() => generateVariants$),
      exhaustMap(() =>
        forkJoin([
          this.productApi.getProductVariants(String(item.id)),
          this.productApi.getProductSpecs(String(item.id)),
        ])
      )
    );
  }

  private createSpecs(specsAndVariants: SpecsAndVariants, item: Product): Observable<ProductSpec>[] {
    if (!specsAndVariants.updateSpecs?.specs?.length) {
      return [of({} as ProductSpec)];
    }

    const createSpecs$ = specsAndVariants.updateSpecs.specs.map(spec => {
      const options = specsAndVariants.updateSpecs.optionsToAdd
        .filter(o => o.specId === spec.id)
        .map(o => {
          o.option.id = undefined; // clear the generated ID
          return o.option;
        });

      // remove mapped options from optionsToAdd to avoid duplication
      options.forEach(o => {
        const index = specsAndVariants.updateSpecs.optionsToAdd.findIndex(
          opt => opt.option.name === o.name && opt.specId === spec.id
        );
        if (index > -1) {
          specsAndVariants.updateSpecs.optionsToAdd.splice(index, 1);
        }
      });

      const productSpec: ProductSpec = {...spec};
      productSpec.spec_options = [...options];

      return this.specApi.create(productSpec, Number(item.id));
    });

    return createSpecs$;
  }

  private createOptions(specsAndVariants: SpecsAndVariants): Observable<SpecOption>[] {
    if (!specsAndVariants.updateSpecs?.optionsToAdd?.length) {
      return [of({} as SpecOption)];
    }

    // Sort by specId to ensure options are created grouped by spec in a stable order
    const sortedOptions = [...specsAndVariants.updateSpecs.optionsToAdd]
      .filter(o => o.specId !== undefined)
      .sort((a, b) => Number(a.specId) - Number(b.specId));

    const createOptions$ = sortedOptions.map(({specId, option}) => {
      option.id = undefined; // clear the generated ID
      return this.specApi.createSpecOption({specId: Number(specId), option: option});
    });
    return createOptions$;
  }

  private updateVariants(specsAndVariants: SpecsAndVariants): Observable<Variation>[] {
    if (!specsAndVariants.variants?.length) {
      return [of({} as Variation)];
    }

    const updateVariants$ = specsAndVariants.variants.map(variant => {
      const itemVariation: Partial<Variation> = {id: Number(variant.id), sku: variant.sku};
      return this.productApi.updateProductVariant(itemVariation);
    });
    return updateVariants$;
  }
}
